// frontend/src/components/common/AppDatePicker.jsx
//
// The app's one date field: every form, dialog and grid cell that picks a day
// renders this (AppDateRangePicker is two of them, DateCellEditor one in a
// grid cell). Picker order and separators come from the user's BCP 47 locale
// via Intl, the calendar's language and first weekday from getPickerLocale,
// standard sizing applied, and accepts/emits 'YYYY-MM-DD' strings so callers
// never have to touch date-fns directly.
//
// `onRefusedChange(refused)` tells a caller that commits the value whether
// the field holds a day it may commit: true while the merchant has typed a
// day it refuses (unfinished, or outside min–max — never emitted, so the
// caller's value still holds the last acceptable day) or while the value it
// was handed is itself outside min–max; false once the field holds an
// acceptable day or is empty.
//
// Exactly one thing names a field: its own `label`, the FieldLine it sits in,
// or `ariaLabel` where no text can sit beside it (a grid cell, named by its
// column header). MUI names the field's group through the input slot's
// `aria-labelledby` / `aria-label` (its hidden input is aria-hidden), so a
// wrapping <label> would name nothing.

import { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../contexts/AuthContext';
import { getDateFnsFormat, getPickerLocale } from '../../utils/formatters';
import { asDate, dateToString, dayInRange } from './dateStringUtils';
import { useFieldLineLabel } from './fieldLineLabel';

function AppDatePicker({
  value,
  onChange,
  onRefusedChange,
  label,
  ariaLabel,
  minDate,
  maxDate,
  required = false,
  disabled = false,
  width = 160,
  slotProps: slotPropsOverride,
  ...rest
}) {
  const { user } = useAuth();
  const labelledBy = useFieldLineLabel();
  if ([label, labelledBy, ariaLabel].filter((name) => name != null).length !== 1) {
    throw new Error('AppDatePicker: give it a label, an ariaLabel or put it in a FieldLine, exactly one of the three');
  }
  const inputName = labelledBy != null ? { 'aria-labelledby': labelledBy }
    : ariaLabel != null ? { 'aria-label': ariaLabel }
    : null;
  const format = getDateFnsFormat(user);
  const { adapterLocale, localeText } = getPickerLocale(user);

  // MUI validates the value it is handed, not the text typed into the field,
  // so a typed day this wrapper refuses would show no error. `refusedAt` is
  // the value the refusal was made against: once the caller hands another
  // value, the field shows that one and the refusal is over.
  const valueKey = value ?? '';
  const [refusedAt, setRefusedAt] = useState(null);
  const typedRefused = refusedAt !== null && refusedAt === valueKey;
  // MUI's own verdict on the handed value (a day before minDate, say).
  const [valueError, setValueError] = useState(null);
  const refused = typedRefused || valueError !== null;
  useEffect(() => {
    onRefusedChange?.(refused);
  }, [refused, onRefusedChange]);

  const mergedSlotProps = {
    ...slotPropsOverride,
    textField: {
      size: 'small',
      required,
      sx: { width },
      ...(typedRefused ? { error: true } : {}),
      ...(slotPropsOverride?.textField || {}),
      ...(inputName ? {
        slotProps: {
          ...slotPropsOverride?.textField?.slotProps,
          input: { ...slotPropsOverride?.textField?.slotProps?.input, ...inputName },
        },
      } : {}),
    },
  };

  const minD = asDate(minDate);
  const maxD = asDate(maxDate);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale} localeText={localeText}>
      <DatePicker
        label={label}
        value={asDate(value) ?? null}
        onChange={(d) => {
          if (d == null) { setRefusedAt(null); onChange?.(''); return; }
          if (dayInRange(d, minD, maxD)) { setRefusedAt(null); onChange?.(dateToString(d)); return; }
          setRefusedAt(valueKey);
        }}
        onError={setValueError}
        minDate={minD}
        maxDate={maxD}
        format={format}
        disabled={disabled}
        slotProps={mergedSlotProps}
        {...rest}
      />
    </LocalizationProvider>
  );
}

export default AppDatePicker;
